import { useEffect, useState, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import { getMomentAccountId, subscribeMomentAccount } from '../moments/accountScope';
import type { VoiceMode } from '../mimo/contracts';
import { roleVoiceStore } from '../mimo/roleVoiceStore';
import type { RoleVoiceProfile } from '../mimo/roleVoiceStore';

export function RoleVoiceEditor({ presetId, mode, autoVoiceDesign = false }: { presetId: string; mode: VoiceMode; autoVoiceDesign?: boolean }): ReactElement {
  const userId = useSyncExternalStore(subscribeMomentAccount, getMomentAccountId);
  return <RoleVoiceFields key={JSON.stringify([userId, presetId])} presetId={presetId} mode={mode} userId={userId} autoVoiceDesign={autoVoiceDesign} />;
}

function RoleVoiceFields({ presetId, mode, userId, autoVoiceDesign }: { presetId: string; mode: VoiceMode; userId: string | null; autoVoiceDesign: boolean }): ReactElement {
  const [profile, setProfile] = useState<RoleVoiceProfile>();
  const [loaded, setLoaded] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const descriptionLabel = autoVoiceDesign ? '角色专属音色参考词' : mode === 'design' ? '角色专属音色描述词' : '角色专属参考音频描述';
  useEffect(() => {
    let active = true;
    setLoaded(false); setProfile(undefined); setConsent(false); setError(null);
    void roleVoiceStore.read(userId, presetId).then(value => {
      if (active) { setProfile(value); setConsent(value?.reference?.consent === true); setLoaded(true); }
    }).catch(() => { if (active) { setLoaded(true); setError('无法读取此设备的角色音色配置，请重新填写'); } });
    return () => { active = false; };
  }, [userId, presetId]);
  const patch = (change: Partial<Omit<RoleVoiceProfile, 'scope'>>): void => {
    setError(null);
    setProfile(current => ({ scope: '', designDescription: '', cloneDescription: '', ...current, ...change }));
    void roleVoiceStore.patch(userId, presetId, change).catch(() => setError('音色配置未能保存在此设备，请重试'));
  };
  return (
    <div className="zs-role-voice">
      <label>
        {descriptionLabel}{autoVoiceDesign ? '' : '（必填）'}
        <textarea aria-label={descriptionLabel} className="zs-input" maxLength={2000} required={!autoVoiceDesign} disabled={!loaded || busy}
          value={(mode === 'design' ? profile?.designDescription : profile?.cloneDescription) ?? ''}
          onChange={event => patch(mode === 'design' ? { designDescription: event.currentTarget.value } : { cloneDescription: event.currentTarget.value })} />
      </label>
      {mode === 'clone' ? <>
        <label><span><input type="checkbox" style={{ width: 'auto' }} checked={consent} disabled={!loaded || busy} onChange={event => {
          const checked = event.currentTarget.checked;
          setConsent(checked);
          if (!checked) patch({ reference: undefined });
        }} /> 我确认已获得参考音色授权</span></label>
        <label>角色专属参考音频（WAV / MP3，最大 7 MB）
          <input type="file" accept=".wav,.mp3,audio/wav,audio/mpeg" aria-label="角色专属参考音频" disabled={!loaded || busy || !consent} onChange={event => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            if (!file) return;
            setBusy(true); setError(null);
            void roleVoiceStore.saveReference(userId, presetId, file, consent).then(reference => {
              setProfile(current => ({ scope: '', designDescription: '', cloneDescription: '', ...current, reference }));
            }).catch(cause => setError(cause instanceof Error ? cause.message : '参考音频未能保存在此设备')).finally(() => setBusy(false));
          }} />
        </label>
        {profile?.reference ? <div className="zs-role-voice-reference"><span title={profile.reference.name}>{profile.reference.name}</span><button type="button" className="zs-button zs-button-ghost" disabled={busy} onClick={() => { patch({ reference: undefined }); setConsent(false); }}>移除音频</button></div> : null}
      </> : null}
      <small>描述词和参考音频均保存在本地</small>
      {error ? <span role="alert" className="zs-role-voice-error">{error}</span> : null}
    </div>
  );
}
